import React from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Avatar } from '@material-ui/core';
import Logo, { useStyles } from '../Logo';
import { FlagsContext, PortalConfigContext } from '../../context';
import { useGetPortalConfigQuery } from '../../services/api';
import { TruncatedText } from '../UI';
import { typography13MediumStyle } from '../Text';
import { RootState } from '../../store';
import { SidebarLogoMenu } from './SidebarLogoMenu';

type Props = {
  isSettingsPage: boolean;
};

export const SidebarLogo = ({ isSettingsPage }: Props) => {
  const { EnableClientPersonalBranding, EnableAuthV2 } = React.useContext(
    FlagsContext,
  );
  const portalConfig = React.useContext(PortalConfigContext);
  const { data: config, isLoading } = useGetPortalConfigQuery();
  const { user, companies } = useSelector(
    (state: RootState) => ({
      user: state.user,
      companies: state.clients?.activeCompanies ?? [],
    }),
    shallowEqual,
  );
  const classes = useStyles();

  const isCompaniesEnabled = !config?.structFields?.disableCompanies ?? false;

  if (isSettingsPage || isLoading || user.loading) return null; // No logo on settings page

  if (
    !user.isClient || // Non client users see the default logo
    !EnableClientPersonalBranding || // Feature is behind the flag.
    (EnableClientPersonalBranding && !isCompaniesEnabled) || // If companies are not enabled, the default logo will be shown
    !companies.length || // If user is not associated with any companies, the default logo will be shown
    !portalConfig?.brand?.allowClientBranding // No branding if feature isn't turned on
  )
    return EnableAuthV2 ? (
      <SidebarLogoMenu>
        <Logo />
      </SidebarLogoMenu>
    ) : (
      <Logo />
    );

  const getLogoProps = () => {
    const company = companies.find(
      x => x.id === user.instance?.attributes['custom:companyName'],
    );
    return {
      logo: company?.fields?.avatarImageURL ?? '',
      name: company?.fields?.name ?? '',
      isPlaceHolderCompany: company?.fields?.isPlaceholder ?? false,
    };
  };

  const { logo, name, isPlaceHolderCompany } = getLogoProps();

  const hasValidImageURL = logo && !isPlaceHolderCompany;

  return (
    <div className={classes.logoWrapper}>
      <Avatar
        variant="rounded"
        className={
          hasValidImageURL
            ? classes.logoContainer
            : classes.noCompanyLogoContainer
        }
      >
        {hasValidImageURL ? (
          <img className={classes.logo} src={logo} alt="Logo" />
        ) : (
          <div>{name.split('')[0]}</div>
        )}
      </Avatar>

      <TruncatedText
        showTitle
        styleProps={{
          ...typography13MediumStyle,
          color: '#fff',
        }}
        text={name}
        maxChars={16.5}
      />
    </div>
  );
};
